import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Loading from './Loading';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import useUser from '../hooks/useUser'; // <-- UNIFIED AUTH HOOK
import PlanChooser from './PlanChooser';
import { getAgentHandoff, getPendingHandoffMessage, hasPendingMessage, setAgentHandoff } from '../utils/aiAgentHandoff';
import {
  buildAuthOverlayRouteState,
  getAuthOverlayBackgroundLocation,
} from '../utils/authOverlayNavigation';
import { isCheckoutPurchasePath } from '../utils/checkoutOverlayNavigation';
import { buildCheckoutAuthPath } from '../utils/checkoutAuthNavigation';
import './ProtectedRoute.css';

/**
 * PROTECTED ROUTE COMPONENT
 * 
 * This component protects routes using the unified authentication system.
 * It works with both Auth0 and custom JWT authentication seamlessly.
 * 
 * AUTHENTICATION FLOW:
 * 1. Checks if user is authenticated via either Auth0 or custom JWT
 * 2. Verifies payment status if required
 * 3. Checks admin status if required
 * 4. Redirects to appropriate login/payment pages if not authorized
 * 
 * SUPPORTED ROUTE TYPES:
 * - Public routes (no authentication required)
 * - Authenticated routes (login required)
 * - Paid routes (login + payment required)
 * - Admin routes (login + admin role required)
 * 
 * CUSTOM JWT INTEGRATION:
 * - Automatically recognizes users logged in via post-purchase JWT
 * - No redirect to Auth0 needed for JWT users
 * - Seamless access to protected content after purchase
 */

type ProtectedRouteProps = React.PropsWithChildren<{
  requirePayment?: boolean;
  adminOnly?: boolean;
  component?: React.ComponentType<Record<string, unknown>>;
  loadingFallback?: React.ReactNode;
  waitForPaymentStatus?: boolean;
  [key: string]: unknown;
}>;

const buildSalesPath = (successUrl?: string | null): string => {
  const searchParams = new URLSearchParams({ showLogin: '1' });
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const fallbackSuccessUrl = currentOrigin ? `${currentOrigin}/ai-agent` : '/ai-agent';
  const trimmedSuccessUrl = successUrl?.trim();

  if (trimmedSuccessUrl) {
    searchParams.set('successUrl', trimmedSuccessUrl);
  } else {
    searchParams.set('successUrl', fallbackSuccessUrl);
  }

  return `/sales?${searchParams.toString()}`;
};

// Add adminOnly prop
const ProtectedRoute = ({
  children,
  requirePayment = false,
  adminOnly = false,
  component: Component,
  loadingFallback,
  waitForPaymentStatus = true,
  ...rest
}: ProtectedRouteProps): React.ReactNode => {
  /**
   * UNIFIED AUTHENTICATION STATE
   * Uses the new unified auth system that handles both Auth0 and custom JWT
   */
  const { 
    isAuthenticated, 
    isLoading: userLoading,
    authLoading,
    profileLoading,
    isAdmin, 
    authMethod,
    logout,
    debugInfo
  } = useUser();
  
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
    error: paymentError,
    requiresSessionRefresh,
    hasResolvedPaymentStatus = false
  } = usePaymentStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const shareSearchParams = new URLSearchParams(location.search);
  // CRITICAL FIX: Check for both private shares (?share=xxx) AND public shares (?public=true)
  // Private shares use a token for access verification
  // Public shares are viewable by anyone (strategy visibility verified downstream in AiBotBuilder)
  const hasShareToken = shareSearchParams.has('share') || shareSearchParams.get('public') === 'true';
  const shareTokenValue = shareSearchParams.get('share');
  const isCheckoutAuthGateRoute = isCheckoutPurchasePath(location.pathname);
  const aiAgentConversationId = location.pathname.match(/^\/ai-agent\/([^/?#]+)/)?.[1] || null;
  const hasPendingAiAgentHandoff =
    location.pathname.startsWith('/ai-agent') &&
    Boolean(
      getAgentHandoff()?.prompt ||
      (aiAgentConversationId && (
        hasPendingMessage(aiAgentConversationId) ||
        getPendingHandoffMessage(aiAgentConversationId)?.prompt
      ))
    );

  // ALWAYS default to assuming user is paying - never show payment notice initially
  const [shouldShowPaymentNotice, setShouldShowPaymentNotice] = useState(false);
  const [stabilityCheckComplete, setStabilityCheckComplete] = useState(false);
  // Add a check for auth challenges
  const [loginAttempted, setLoginAttempted] = useState(false);

  /**
   * LOADING STATE CALCULATION
   * Determines when the component should show loading spinner vs actual content
   */
  // For adminOnly routes, also wait until isAdmin is determined (not null)
  const isPaymentStatusUnknown =
    requirePayment &&
    isAuthenticated &&
    !hasShareToken &&
    !paymentLoading &&
    Boolean(paymentError) &&
    !requiresSessionRefresh &&
    !hasResolvedPaymentStatus;
  const isAuthLoading = authLoading ?? userLoading;
  const isProfileInfoLoading = profileLoading ?? (adminOnly ? userLoading : false);
  const isCoreLoading =
    isAuthLoading ||
    (waitForPaymentStatus && (paymentLoading || isPaymentStatusUnknown));
  const isAdminStatusPending = adminOnly && (isProfileInfoLoading || isAdmin === null);
  const isLoading = isCoreLoading || isAdminStatusPending;

  /**
   * COMPREHENSIVE DEBUG LOGGING
   * Tracks authentication state for troubleshooting protected route issues
   */
  console.debug('[ProtectedRoute] Route protection analysis:', {
    // Route requirements
    requirePayment,
    adminOnly,
    waitForPaymentStatus,
    path: location.pathname,

    // Authentication state
    isAuthenticated,
    authMethod,
    isAdmin,

    // Payment state
    isPayingCustomer,
    paymentError,
    requiresSessionRefresh,
    hasResolvedPaymentStatus,
    isPaymentStatusUnknown,

    // Loading states
    userLoading,
    authLoading: isAuthLoading,
    profileLoading: isProfileInfoLoading,
    paymentLoading,
    isCoreLoading,
    isAdminStatusPending,
    isLoading,

    // Component state
    shouldShowPaymentNotice,
    stabilityCheckComplete,
    loginAttempted,

    // Debug info
    debugInfo
  });
  
  /**
   * RESET LOGIN ATTEMPT STATE ON ROUTE CHANGE
   * Ensures Safari/Brave browser compatibility on navigation
   */
  useEffect(() => {
    setLoginAttempted(false);
  }, [location.pathname]);

  /**
   * SAFARI/BRAVE BROWSER AUTH FIX
   * Some browsers require special handling for Auth0 authentication
   */
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated && !loginAttempted) {
      const ua = navigator.userAgent;
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const isBrave = ua.includes('Brave'); // More robust check for Brave

      // Determine if the route inherently requires authentication for access,
      // beyond just being a paid feature.
      // - Admin routes always require auth.
      // - Standard protected routes (not requiring payment, not admin) require auth.
      // - Routes that *only* require payment but might otherwise be accessible to guests
      //   (e.g., a sales page leading to a paid feature) should NOT trigger this
      //   specific Safari/Brave early login redirect. The main logic below will handle
      //   redirecting unauthenticated users to /sales for such routes.
      const routeNeedsAuth = (adminOnly || !requirePayment) && !isCheckoutAuthGateRoute;

      if (routeNeedsAuth && (isSafari || isBrave)) {
        console.debug('[ProtectedRoute] Safari/Brave auth fix for auth-required route → hard redirect to /login');
        setLoginAttempted(true);
        // Include the full path with query parameters to preserve share links
        const fullPath = location.pathname + location.search;
        navigate(
          `/login?returnTo=${encodeURIComponent(fullPath)}`,
          {
            replace: true,
            state: buildAuthOverlayRouteState(getAuthOverlayBackgroundLocation(location.state) || location),
          }
        );
      }
    }
  }, [adminOnly, isAuthenticated, isAuthLoading, isCheckoutAuthGateRoute, loginAttempted, location.pathname, location.search, navigate, requirePayment]);

  // We'll use this effect to wait until EVERYTHING is stable before showing notice
  useEffect(() => {
    // If we don't require payment, do nothing
    if (!requirePayment) return;
    if (hasShareToken) return;
    if (requiresSessionRefresh) {
      setStabilityCheckComplete(false);
      setShouldShowPaymentNotice(false);
      return;
    }
    
    // Reset stability check when loading state changes
    if (paymentLoading) {
      setStabilityCheckComplete(false);
      setShouldShowPaymentNotice(false);
      return;
    }

    if (isPaymentStatusUnknown) {
      setStabilityCheckComplete(false);
      setShouldShowPaymentNotice(false);
      return;
    }
    
    // Only after payment status check is complete, wait a significant time
    // to make absolutely sure everything has settled
    const timer = setTimeout(() => {
      setStabilityCheckComplete(true);
      
      // Only now, after waiting, if user is confirmed not paying, show notice
      if (!isPayingCustomer && !paymentLoading) {
        console.debug('ProtectedRoute: Showing payment notice after stability check');
        setShouldShowPaymentNotice(true);
      }
    }, 1000); // Much longer delay (1 second) to be absolutely sure
    
    return () => clearTimeout(timer);
  }, [paymentLoading, isPayingCustomer, requiresSessionRefresh, requirePayment, hasShareToken, isPaymentStatusUnknown]);
  
  // Always hide payment notice if user becomes a paying customer
  useEffect(() => {
    if (isPayingCustomer) {
      setShouldShowPaymentNotice(false);
    }
  }, [isPayingCustomer]);

  // Add detailed logging - MOVED UP before any conditionals
  useEffect(() => {
    if (requirePayment || hasShareToken) {
      console.debug('ProtectedRoute auth state:', {
        isAuthenticated,
        isPayingCustomer,
        hasShareToken,
        shareToken: shareTokenValue ? `${shareTokenValue.substring(0, 8)}...` : undefined,
        shouldRenderPaymentNotice: requirePayment && stabilityCheckComplete && shouldShowPaymentNotice,
        paymentError,
        requiresSessionRefresh,
        hasResolvedPaymentStatus,
        isPaymentStatusUnknown,
        path: location.pathname,
        loading: { user: userLoading, auth: isAuthLoading, profile: isProfileInfoLoading, payment: paymentLoading }
      });
    }
  }, [isAuthenticated, isPayingCustomer, requirePayment, stabilityCheckComplete,
      shouldShowPaymentNotice, location.pathname, userLoading, isAuthLoading, isProfileInfoLoading, paymentLoading, location.search, paymentError, requiresSessionRefresh, hasResolvedPaymentStatus, isPaymentStatusUnknown, hasShareToken, shareTokenValue]);

  // If a component prop is passed, render it, otherwise render children
  const elementToRender = Component ? <Component {...(rest as Record<string, unknown>)} /> : children;

  // Allow share links to bypass auth/paywall requirements (read-only access handled downstream)
  if (hasShareToken && !adminOnly) {
    if (isLoading) {
      console.debug('ProtectedRoute: Skipping loading screen for share-token access.');
    }
    return elementToRender;
  }

  // Show loading during any check
  if (isLoading) {
    if (hasPendingAiAgentHandoff && isAuthenticated) {
      console.debug('ProtectedRoute: Rendering AI Agent during pending handoff loading window.');
      return elementToRender;
    }
    console.debug('ProtectedRoute: Showing Loading component.', { isCoreLoading, isAdminStatusPending });
    return loadingFallback ?? <Loading />;
  }

  // --- CHECKS AFTER LOADING IS COMPLETE ---

  // 1. Check Authentication
  if (!isAuthenticated) {
    // Newsletter CTAs land at /ai-agent?prompt=<question>&autoGenerate=true
    // for unauthenticated users. The auth round-trip (sales → login → Auth0
    // → callback → returnTo) is fragile and can drop URL params, leaving the
    // user on a blank /ai-agent after login. Stash the prompt into the
    // existing handoff sessionStorage so AiAgent's handoff path picks it up
    // and auto-sends after auth lands. sessionStorage survives the Auth0
    // redirect because it is tab-scoped, not page-scoped, on the originating
    // domain.
    //
    // Only stash when autoGenerate=true is also present — otherwise we'd
    // auto-send for callers who just wanted to prefill the input. The
    // handoff mechanism inherently auto-sends, so opting in via
    // autoGenerate=true keeps the contract explicit.
    if (location.pathname.startsWith('/ai-agent')) {
      const promptParam = shareSearchParams.get('prompt');
      const wantsAutoSend = shareSearchParams.get('autoGenerate') === 'true';
      if (wantsAutoSend && promptParam && promptParam.trim()) {
        try {
          setAgentHandoff({ prompt: promptParam });
          console.debug('[ProtectedRoute] Stashed AI agent prompt into handoff before auth redirect (length=' + promptParam.length + ')');
        } catch (err) {
          console.warn('[ProtectedRoute] Failed to stash AI agent prompt into handoff', err);
        }
      }
    }

    if (requirePayment) {
      // For premium features, send unauthenticated users to sales
      console.debug('ProtectedRoute: Redirecting unauthenticated user to sales for paid feature (after loading).');
      // Include the full path with query parameters to preserve share links
      const fullPath = location.pathname + location.search;
      const successUrl = `${window.location.origin}${fullPath}`;
      return (
        <Navigate
          to={buildSalesPath(successUrl)}
          replace
          state={buildAuthOverlayRouteState(getAuthOverlayBackgroundLocation(location.state) || location)}
        />
      );
    } else {
      if (isCheckoutAuthGateRoute) {
        const fullPath = location.pathname + location.search + location.hash;
        return (
          <Navigate
            to={buildCheckoutAuthPath(fullPath)}
            replace
            state={location.state}
          />
        );
      }

      // For standard protected routes, redirect to login
      console.debug('ProtectedRoute: Redirecting unauthenticated user to login (after loading).');
      // Include the full path with query parameters to preserve share links
      const fullPath = location.pathname + location.search;
      return (
        <Navigate
          to={`/login?returnTo=${encodeURIComponent(fullPath)}`}
          replace
          state={buildAuthOverlayRouteState(getAuthOverlayBackgroundLocation(location.state) || location)}
        />
      );
    }
  }

  // --- User IS Authenticated ---

  // 2. Check Admin Requirement (only if adminOnly is true)
  // Now that isLoading is false, isAdmin should be either true or false (not null) for admin routes
  if (adminOnly && !isAdmin) {
    console.debug(`ProtectedRoute: Redirecting authenticated non-admin user (isAdmin: ${isAdmin}) from admin route '${location.pathname}' (after loading).`);
    // Redirect non-admins to the home page (or a suitable 'unauthorized' page)
    return <Navigate to="/" replace />;
  }

  // --- User is Authenticated and meets Admin requirements (if any) ---

  // 3. Check Payment Requirements (only if requirePayment is true)
  // IMPORTANT: Share links bypass payment requirements

  const shouldRefreshSession = requirePayment && !paymentLoading && requiresSessionRefresh && !hasShareToken;

  if (shouldRefreshSession) {
    return (
      <div className="protected-route-session-error">
        <div className="protected-route-session-error-card">
          <FontAwesomeIcon icon={faExclamationCircle} className="protected-route-session-error-icon" />
          <h2>Refresh Your Session</h2>
          <p>
            We could not verify your subscription because your browser session stopped returning an API token.
            Sign in again to reload your account access.
          </p>
          <button
            className="protected-route-session-error-button"
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign in again
          </button>
          {paymentError && (
            <div className="protected-route-session-error-detail">
              {String(paymentError)}
            </div>
          )}
        </div>
      </div>
    );
  }

  const shouldBlockForPayment = requirePayment && !paymentLoading && !isPaymentStatusUnknown && !isPayingCustomer && !hasShareToken;

  if (shouldBlockForPayment) {
    // Context-specific messaging based on the route being accessed
    let heading = 'Upgrade Required';
    let body = 'This feature is not included in your current plan. Upgrade to unlock full access.';

    if (location.pathname.includes('/ai-agent')) {
      heading = 'Upgrade Required';
      body = 'AI Agent access is not included in your current plan. Upgrade to start building your trading strategy.';
    } else if (location.pathname.includes('/add-bot')) {
      heading = 'Upgrade Required';
      body = 'Bot deployment is not included in your current plan. Upgrade to deploy your strategies to live trading.';
    }

    return <PlanChooser heading={heading} body={body} />;
  }

  return (
    <>
      {/* Legacy banner path retained for any flows that opt into delayed notice */}
      {requirePayment && stabilityCheckComplete && !isPayingCustomer && !hasShareToken && (
        <div className="payment-notice-bar">
          <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '8px' }} />
          <span>
            This feature is available for paying customers only. Please subscribe in order to generate or refine strategies.
          </span>
          <button
            className="subscribe-button"
            onClick={() => {
              const fullPath = location.pathname + location.search;
              navigate(`/sales?showLogin=1&successUrl=${encodeURIComponent(fullPath)}`, {
                state: buildAuthOverlayRouteState(getAuthOverlayBackgroundLocation(location.state) || location),
              });
            }}
          >
            Subscribe Now
          </button>
        </div>
      )}
      {elementToRender} {/* Render the component or children */}
    </>
  );
};

export default ProtectedRoute;
